import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useInvoice, useVoidInvoice } from '../../hooks/useInvoices';
import { useAuthStore } from '../../stores/auth.store';
import { formatCurrency } from '../../lib/currency';
import PrintButton from '../../components/common/PrintButton';
import type { InvoiceLineItem } from '@motra/shared';

export default function InvoiceDetail() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const shop = useAuthStore((s) => s.shop);
  const currSym = shop?.curr_sym ?? '$';

  const { data: invoice, isLoading } = useInvoice(id);
  const voidInvoice = useVoidInvoice();

  if (isLoading) {
    return <div className="page"><p>Loading invoice...</p></div>;
  }

  if (!invoice) {
    return (
      <div className="page">
        <p>Invoice not found.</p>
        <button className="btn btn--outline" onClick={() => navigate('/invoices')}>
          {t('back', 'Back')}
        </button>
      </div>
    );
  }

  let lineItems: InvoiceLineItem[] = [];
  try {
    lineItems = invoice.items ? JSON.parse(invoice.items) : [];
  } catch { /* empty */ }

  async function handleVoid() {
    if (!id) return;
    if (!confirm('Void this invoice? This cannot be undone.')) return;
    await voidInvoice.mutateAsync({ invoiceId: id, reason: 'Manual void' });
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Invoice #{invoice.id}</h1>
          <span
            className={`badge badge--${
              invoice.status === 'completed'
                ? 'success'
                : invoice.status === 'voided'
                ? 'danger'
                : 'neutral'
            }`}
          >
            {invoice.status}
          </span>
        </div>
        <div className="page-header__actions">
          <button className="btn btn--outline" onClick={() => navigate('/invoices')}>
            {t('back', 'Back')}
          </button>
          <PrintButton label={t('print', 'Print')} />
          {invoice.status === 'completed' && (
            <button
              className="btn btn--danger"
              onClick={handleVoid}
              disabled={voidInvoice.isPending}
            >
              {t('void', 'Void')}
            </button>
          )}
        </div>
      </div>

      {/* Invoice meta */}
      <div className="invoice-meta form-card">
        <div className="invoice-meta__grid">
          <div>
            <span className="invoice-meta__label">{t('date', 'Date')}</span>
            <span className="invoice-meta__value">
              {invoice.created_at ? new Date(invoice.created_at).toLocaleString() : '—'}
            </span>
          </div>
          <div>
            <span className="invoice-meta__label">{t('payment_type', 'Payment')}</span>
            <span className="invoice-meta__value">{invoice.payment_type ?? '—'}</span>
          </div>
          <div>
            <span className="invoice-meta__label">Customer</span>
            <span className="invoice-meta__value">{invoice.customer_id ?? '—'}</span>
          </div>
          {invoice.payment_ref && (
            <div>
              <span className="invoice-meta__label">Payment Ref</span>
              <span className="invoice-meta__value">{invoice.payment_ref}</span>
            </div>
          )}
        </div>
      </div>

      {/* Line items */}
      <div className="invoice-items form-card">
        <h3 className="form-section-title">Items</h3>
        <table className="data-table">
          <thead>
            <tr>
              <th>{t('name', 'Name')}</th>
              <th>{t('quantity', 'Qty')}</th>
              <th>{t('price', 'Price')}</th>
              <th>{t('discount', 'Discount')}</th>
              <th>{t('total', 'Total')}</th>
            </tr>
          </thead>
          <tbody>
            {lineItems.map((item, i) => {
              const lineTotal = item.price * item.qty;
              const discountAmt =
                item.discount
                  ? item.discount_type === 'percentage'
                    ? lineTotal * (item.discount / 100)
                    : item.discount * item.qty
                  : 0;
              return (
                <tr key={i}>
                  <td>{item.name}</td>
                  <td>{item.qty}</td>
                  <td>{formatCurrency(item.price, currSym)}</td>
                  <td>
                    {item.discount
                      ? item.discount_type === 'percentage'
                        ? `${item.discount}%`
                        : formatCurrency(item.discount, currSym)
                      : '—'}
                  </td>
                  <td>{formatCurrency(lineTotal - discountAmt, currSym)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div className="invoice-totals form-card">
        <div className="invoice-totals__row">
          <span>{t('subtotal', 'Subtotal')}</span>
          <span>{formatCurrency(invoice.subtotal ?? 0, currSym)}</span>
        </div>
        {invoice.discount_val != null && invoice.discount_val > 0 && (
          <div className="invoice-totals__row invoice-totals__row--discount">
            <span>{t('discount', 'Discount')}</span>
            <span>−{formatCurrency(invoice.discount_val, currSym)}</span>
          </div>
        )}
        {invoice.tax_val != null && invoice.tax_val > 0 && (
          <div className="invoice-totals__row">
            <span>
              {t('tax', 'Tax')} {invoice.tax != null ? `(${invoice.tax}%)` : ''}
            </span>
            <span>{formatCurrency(invoice.tax_val, currSym)}</span>
          </div>
        )}
        <div className="invoice-totals__row invoice-totals__row--total">
          <span>{t('total', 'Total')}</span>
          <span>{formatCurrency(invoice.total ?? 0, currSym)}</span>
        </div>
        {invoice.tendered != null && (
          <div className="invoice-totals__row">
            <span>{t('tendered', 'Tendered')}</span>
            <span>{formatCurrency(invoice.tendered, currSym)}</span>
          </div>
        )}
        {invoice.change != null && invoice.change > 0 && (
          <div className="invoice-totals__row">
            <span>{t('change', 'Change')}</span>
            <span>{formatCurrency(invoice.change, currSym)}</span>
          </div>
        )}
      </div>

      {invoice.note && (
        <div className="form-card">
          <h3 className="form-section-title">{t('notes', 'Notes')}</h3>
          <p>{invoice.note}</p>
        </div>
      )}
    </div>
  );
}
